var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import securityApi from "apis/eduidSecurity";
import { Splash } from "components/Common/Splash";
import { useAppSelector } from "eduid-hooks";
import { ACCOUNT_PATH, CHPASS_BASE_PATH } from "helperFunctions/paths";
import { useCallback, useEffect, useRef, useState } from "react";
import { Form as FinalForm } from "react-final-form";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import { ChangePasswordCustomForm } from "./ChangePasswordCustom";
import { ChangePasswordRadioOption } from "./ChangePasswordRadioOption";
import { ChangePasswordSuggestedForm } from "./ChangePasswordSuggested";
export function ChangePassword() {
    const is_app_loaded = useAppSelector((state) => state.config.is_app_loaded);
    const suggested = useAppSelector((state) => state.chpass.suggested_password);
    const [renderSuggested, setRenderSuggested] = useState(true); // toggle display of custom or suggested password forms
    const navigate = useNavigate();
    const isMounted = useRef(true);
    const [fetchSuggestedPassword] = securityApi.useLazyFetchSuggestedPasswordQuery();
    const [changePassword] = securityApi.useLazyChangePasswordQuery();
    const handleSuggestedPassword = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const response = yield fetchSuggestedPassword();
        if (isMounted.current) {
            if (response.isSuccess) {
                navigate(CHPASS_BASE_PATH);
            }
        }
    }), [fetchSuggestedPassword, navigate]);
    const handleSubmitNewPassword = useCallback((values) => __awaiter(this, void 0, void 0, function* () {
        const newPassword = renderSuggested ? values.suggested : values.custom;
        if (newPassword) {
            const response = yield changePassword({ new_password: newPassword });
            if (response.isSuccess) {
                navigate(`${CHPASS_BASE_PATH}/success`, {
                    state: { password: newPassword, isSuggested: renderSuggested },
                });
            }
        }
    }), [renderSuggested, changePassword, navigate]);
    const handleCancel = useCallback((event) => {
        // Callback from sub-component when the user clicks on the button to abort changing password
        event.preventDefault();
        navigate(ACCOUNT_PATH);
    }, [navigate]);
    const initialValues = { suggested };
    const handleSwitchChange = useCallback(() => {
        setRenderSuggested((prev) => !prev);
    }, []);
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);
    useEffect(() => {
        if (is_app_loaded && suggested === undefined && !location.pathname.includes("success")) {
            handleSuggestedPassword();
        }
    }, [suggested, is_app_loaded, handleSuggestedPassword]);
    return (_jsx(FinalForm, { onSubmit: handleSubmitNewPassword, initialValues: initialValues, render: (formProps) => {
            const child_props = { formProps };
            return (_jsxs(Splash, { showChildren: Boolean(suggested), children: [renderSuggested ? (_jsxs("section", { className: "intro", children: [_jsx("h1", { children: _jsx(FormattedMessage, { id: "changePassword.suggested", description: "Change password - headline", defaultMessage: "Change password: Suggested password" }) }), _jsx("div", { className: "lead", children: _jsx("p", { children: _jsx(FormattedMessage, { id: "changePassword.generatedLead", description: "Generated password - lead", defaultMessage: "A strong password has been generated for you. To proceed you will need to copy the password in to the Repeat new password field and click the Save button to store it for future use." }) }) })] })) : (_jsxs("section", { className: "intro", children: [_jsx("h1", { children: _jsx(FormattedMessage, { id: "changePassword.custom", description: "Change password - headline", defaultMessage: "Change password: Set your own password" }) }), _jsx("div", { className: "lead", children: _jsx("p", { children: _jsx(FormattedMessage, { id: "changePassword.lead", description: "Strong password - lead", defaultMessage: "When creating your own password, make sure it's strong enough to keep your accounts safe." }) }) })] })), _jsx(ChangePasswordRadioOption, { handleSwitchChange: handleSwitchChange, renderSuggested: renderSuggested }), renderSuggested ? (_jsx(ChangePasswordSuggestedForm, Object.assign({}, child_props, { handleCancel: handleCancel, suggestedPassword: suggested }))) : (_jsx(ChangePasswordCustomForm, Object.assign({}, child_props, { handleCancel: handleCancel, handleSubmit: handleSubmitNewPassword })))] }));
        } }));
}
